import { PrintDataStructure } from '../../../models/sift/json/print-model';
import {
  TrimImageBase,
  TrimLaboOrderImage,
  TrimLaboOrderPrintModel,
  TrimLaboOrderPrintModelCreator,
} from './creator/trim.labo-order.print-model.creator';
import PixelConverter, { WH } from '../../../utilities/pixel-converter';
import { PrintTrimData } from '../../model/trim.print-model';
import { Pos } from '../../../models/pos';
import { TrimDataManager } from '../../manager/data/trim-data.manager';
import { TrimPrintManager } from '../../print/trim.print';

/**
 * おまかせトリミング(トリミング画面を経由しない場合)の発注データ生成。
 *
 * NOTE: 本番で使われるのはバックエンドの同名クラスで、こちらは検証用の写しである。
 *       trim-data.manager.ts から呼ばれるが戻り値は破棄される。
 *       挙動を確認する目的で参照されるため、バックエンド
 *       (ppm-cloud-api: server/scripts/src/modules/factory/labo-order/) を
 *       変更したら必ずこちらも同期すること。食い違うと誤読の元になる。
 */
export class TrimLaboOrderFactory {
  private readonly FIT_NUM = 5;
  private IMAGE_SET: 'out' | 'in' | 'eq' = 'out';
  private printModel!: TrimLaboOrderPrintModel;

  constructor() {}

  /**
   * こいつを呼び出せばjsonにして返す
   * @param {{kijshopCd: string, shopOrderId: string, orderId: string, image: TrimLaboOrderImage, structure: PrintDataStructure}} param
   * @returns {Promise<{filename: string, body: string}>}
   */
  async createTrimJson(param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
    image: TrimImageBase,
    structure: PrintDataStructure,
  }) {
    const creator = new TrimLaboOrderPrintModelCreator();
    this.printModel = await creator.create(param);
    const trimData = await this.trim();
    return trimData.exportJson();
  }

  // - Trimming -
  private async trim() {
    const { base, item, image, printTrimData } = this.printModel;
    const mainFrame = base.getImageWindow();
    if (!mainFrame) {
      throw new Error('printModel is notFound !!!!!!!!!')
    }
    const {virtual} = mainFrame;
    const { wh} = virtual;
    // -- 外接・内接・px等倍 --
    this.IMAGE_SET = TrimPrintManager.getTrimBaseType(item.productData?.meta?.trimBaseType ?? 1);
    // -- リサイズ（写真フィット機能込み） --
    const scale = await this.resizeImage(image, wh.height, wh.width);
    // -- 真ん中にずらすための移動量を取得 --
    // -- フォーマットに必要なデータ整形 --
    // const _base = printTrimData.image.frame || printTrimData.temp.base;
    // const tempSize: WH = {width: PixelConverter.mmToPx(_base.width) / TrimDataManager.getAdditionalValue, height: PixelConverter.mmToPx(_base.height) / TrimDataManager.getAdditionalValue};


    const _base = printTrimData.temp.base;
    const frame = printTrimData.image.frame || { ...printTrimData.temp.base, x: 0, y: 0 };
    const tempSize: WH = {width: PixelConverter.mmToPx(_base.width - (frame.x * 2)) / TrimDataManager.getAdditionalValue, height: PixelConverter.mmToPx(_base.height - (frame.y * 2)) / TrimDataManager.getAdditionalValue};
    const z = PixelConverter.mmToPx(_base.width) / Number(image.virtual.width);

    const x = await this.centeringImage(image.virtual.width, tempSize.width, scale);
    const y = await this.centeringImage(image.virtual.height, tempSize.height, scale);
    // -- フォーマット --
    this.formatTrimData(tempSize, z, {x , y}, scale, printTrimData);
    // -- 参照が効いてるのでそのまま返す --
    return printTrimData;
  }
  // - pxとmmの変換と座標系の変換 -
  private formatTrimData(baseSize: WH, z: number, movePos: Pos, scale: number, printTrimData: PrintTrimData) {
    const maskW = (baseSize.width / 2 / scale) + (movePos.x / scale);
    const maskH = (baseSize.height / 2 / scale) + (movePos.y / scale);

    printTrimData.image.x = PixelConverter.pxToMm((maskW) * z) - (printTrimData.image.frame?.x ?? 0);
    printTrimData.image.y = PixelConverter.pxToMm((maskH) * z) - (printTrimData.image.frame?.y ?? 0);
    printTrimData.image.width = PixelConverter.pxToMm(baseSize.width / scale * z);
    printTrimData.image.height = PixelConverter.pxToMm(baseSize.height / scale * z);
  }

  // - 画像を中心に配置する -
  private async centeringImage(imageSize: number, maskSize: number, imageScale: number) {
    if (!maskSize || imageSize * imageScale === maskSize) return 0;
    // return (imageSize - maskSize) / 2;
    return (imageSize * imageScale - maskSize) / 2;
  }

  // - 初期サイズの変更 -
  private async resizeImage(image: TrimLaboOrderImage, minHeight: number, minWidth: number) {
    // -- フレームへの収まり具合で処理を分岐 --
    if (!minHeight || !minWidth) return 0;
    const fitNum = this.FIT_NUM * 2;
    let scaleX = (minWidth + fitNum) / image.virtual.width;
    let scaleY = (minHeight + fitNum) / image.virtual.height;
    scaleX = Math.round(scaleX * 100) / 100;
    scaleY = Math.round(scaleY * 100) / 100;
    switch (this.IMAGE_SET) {
      case 'in':
        return (scaleY < scaleX ? scaleY : scaleX);
      case 'out':
        return (scaleY > scaleX ? scaleY : scaleX);
      case 'eq':
        /* 実解像度とサムネイルの比。アスペクト比ではないので注意 */
        const scale = image.real.width / image.virtual.width / TrimDataManager.getAdditionalValue;
        return Math.round(scale * 100) / 100;
    }

  }

}
